import { SelectChangeEvent, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InfoSection from "app/components/InfoSection";
import AgeRestriction from "assets/icons/age_restriction.svg";
import CancellationIcon from "assets/icons/cancellation.svg";
import CapacityIcon from "assets/icons/capacity.svg";
import DarkLogo from "assets/icons/dark-logo.svg";
import FireIcon from "assets/icons/fire.svg";
import RedFireIcon from "assets/icons/fire_icon.svg";
import LocationIcon from "assets/icons/location_icon.svg";
import TimeIcon from "assets/icons/time.svg";
import dayjs, { Dayjs } from "dayjs";
import { useGetBusinessesByUserIdQuery } from "lib/features/business/businessService";
import checkoutServices, {
  Image,
  Payment,
  ProductOption,
  Referral,
} from "lib/features/checkout/checkoutService";
import { setItemsLoading } from "lib/features/item/itemsSlice";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import "react-phone-number-input/style.css";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useLocation, useNavigate } from "react-router-dom";
import { availabilityCheckoutStyles as styles } from "styles/Checkout/availabilityCheckout.styles";
import { calculateFees } from "util/calculateFees";
import { settings } from "util/carouselSettings";
import {
  checkoutDate,
  checkoutTime,
  formatDurationTime,
} from "util/commonHelper";
import { formatAddress } from "util/formatAddress";
import { formatAgeRestriction } from "util/formatAgeRestriction";
import { currencyFormat } from "util/formatCurrency";
import { parseImageUrl } from "util/parseImageUrl";
import calender from "../../../assets/icons/calendar_icon.svg";
import clock from "../../../assets/icons/clock_black.svg";
import ListIcon from "../../../assets/icons/list.svg";
import AdditionalInformation from "./AdditionalInformation";
import "./Checkout.css";
import { BookingDTO } from "./CheckoutModels";
import CustomerInformationForm from "./CustomerInformationForm";
import DateAndTime from "./DateAndTime";
import StripePayment from "./StripeCheckoutModal";
import SuccessModal from "./SuccessModal";

interface AvailabilityCheckoutProps {
  referral: Referral;
  comment: string;
  setComment: (value: string) => void;
  onMonthChange: (date: Dayjs) => void;
  isLoadingAvailabilities: boolean;
}

interface InfluencerType {
  id: string;
  type: string;
}

interface FormData {
  group_type: string;
}

interface ReferredByUserData {
  name: string;
  avatar?: string;
  isBusiness?: boolean;
}

const influencerTypesData: InfluencerType[] = [
  { id: "bachelorette", type: "Bachelorette Party" },
  { id: "corporate", type: "Corporate Event" },
  { id: "birthday", type: "Birthday Celebration" },
];

export default function AvailabilityCheckout({
  referral,
  comment,
  setComment,
  onMonthChange,
  isLoadingAvailabilities,
}: AvailabilityCheckoutProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showBack = queryParams.get("showBack");

  const [showCheckout, setShowCheckout] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [price, setPrice] = useState<number>(
    referral.item?.itemDetails[0]?.price,
  );
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false);
  // default date should be the referral availability start time
  // or today, whichever is later
  const [selectedDate, setSelectedDate] = useState<Dayjs>(
    dayjs.max(dayjs.utc(referral?.availability?.start_time), dayjs()),
  );
  const [bookingId, setBookingId] = useState<number | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<number>(1);

  const [intent, setIntent] = useState<string>("");
  const [referredByUserData, setReferredByUserData] =
    useState<ReferredByUserData | null>(null);

  const [selectedTime, setSelectedTime] = useState(
    moment.utc(referral?.availability?.start_time).local().format("hh:mm A"),
  );
  const formattedAgeRestriction = formatAgeRestriction(
    referral?.item?.itemDetails[0]?.min_age,
    referral?.item?.itemDetails[0]?.max_age,
  );

  const [avtobook, setAvtobook] = useState(referral.availability);

  const { data: referredByBusinessData } = useGetBusinessesByUserIdQuery(
    { id: referral?.user?.id },
    { skip: !referral?.user?.id || referral?.user?.role_id !== 4 },
  );
  const availableTimes = useMemo(() => {
    return (
      referral?.item?.availabilities
        ?.filter(
          (f) =>
            moment(f?.start_time).format("YYYY-MM-DD") ===
            selectedDate.format("YYYY-MM-DD"),
        )
        .sort((a, b) => {
          const timeA = moment.utc(a.start_time).local();
          const timeB = moment.utc(b.start_time).local();
          if (timeA.hour() === 0) return 1;
          if (timeB.hour() === 0) return -1;
          return timeA.diff(timeB);
        })
        .map((availability) =>
          moment.utc(availability.start_time).local().format("hh:mm A"),
        ) ?? []
    );
  }, [referral?.item?.availabilities, selectedDate]);

  // Sync selectedTime when availableTimes changes
  useEffect(() => {
    if (availableTimes.length > 0 && !availableTimes.includes(selectedTime)) {
      setSelectedTime(availableTimes[0]);
    }
  }, [availableTimes]);

  const [formData, setFormData] = useState<FormData>({
    group_type: influencerTypesData[0]?.id || "",
  });

  useEffect(() => {
    const fetchReferredByData = async () => {
      if (referral?.user) {
        const isBusiness = referral.user.role_id === 4;

        if (isBusiness && referredByBusinessData) {
          const businesses = Array.isArray(referredByBusinessData)
            ? referredByBusinessData
            : [referredByBusinessData];

          if (businesses.length > 0) {
            const business = businesses[0];
            setReferredByUserData({
              name:
                business?.business ||
                `${referral.user.first_name} ${referral.user.last_name}`,
              avatar: business?.businessImages?.[0]?.uri
                ? parseImageUrl(business.businessImages[0].uri)
                : referral.user.avatar
                  ? parseImageUrl(referral.user.avatar)
                  : undefined,
              isBusiness: true,
            });
          } else {
            setReferredByUserData({
              name: `${referral.user.first_name} ${referral.user.last_name}`,
              avatar: referral.user.avatar
                ? parseImageUrl(referral.user.avatar)
                : undefined,
              isBusiness: true,
            });
          }
        } else {
          setReferredByUserData({
            name: `${referral.user.first_name} ${referral.user.last_name}`,
            avatar: referral.user.avatar
              ? parseImageUrl(referral.user.avatar)
              : undefined,
            isBusiness: false,
          });
        }
      }
    };

    fetchReferredByData();
  }, [referral?.user, referredByBusinessData]);

  const handleSelect = (event: SelectChangeEvent) => {
    setFormData((prevData) => ({
      ...prevData,
      group_type: event.target.value as string,
    }));
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedDate(dayjs(date));
      const filteredAvailabilities = referral?.item?.availabilities?.filter(
        (availability) => {
          const availabilityDate = moment(availability.start_time).format(
            "YYYY-MM-DD",
          );
          const selectedDateStr = moment(date.toDate()).format("YYYY-MM-DD");
          return availabilityDate === selectedDateStr;
        },
      );

      setAvtobook(filteredAvailabilities[0]);
    }
    setIsCalendarVisible(false);
  };

  const setAdditionalOptionsValues = (options: ProductOption[]) => {
    referral.item.additionalOptions = options;
  };

  const handleBusinessShare = () => {
    navigate(
      `/business/${referral?.item?.business?.id}?referredBy=${referral.user.id}`,
    );
  };

  const handleBookNowClick = () => setShowCheckout(true);

  async function handleCheckoutClick(intentId: string) {
    const payment: Payment = { name, phone: phoneNumber || "", email };
    const booking: BookingDTO = {
      availability_quantity: selectedTicket,
      availability_id: avtobook.id,
      reservation_time: avtobook.start_time,
      referral_id: referral.id,
      stripe_checkout_link: "",
      state: 2,
      booking_time: new Date(),
      customer_total: price + calculateFees(price),
      business_payout: 0,
      commission_payout: 0,
      platform_fee: 0,
      bookingOptions: referral.item.additionalOptions,
      payment_info: payment,
      payment_id: intentId,
      comment: comment,
    };

    try {
      const result = (await checkoutServices.createCheckoutSession(booking))
        .data;
      if (!result.success) throw new Error(result.message);
      setBookingId(result.booking_id);
      return result?.result?.clientSecret;
    } catch (err: any) {
      alert(err.message);
      console.error(JSON.stringify(err));
    }
  }

  async function handleGetStripeClientSecret() {
    try {
      const result = (
        await checkoutServices.getStripeToken(price + calculateFees(price))
      ).data;
      setIntent(result?.id);
      return result;
    } catch (err: any) {
      alert(err.message);
      console.error(JSON.stringify(err));
    }
  }

  return (
    <>
      {!showSuccess && (
        <>
          <Box sx={styles.container}>
            <Box sx={styles.header}>
              {showBack === "true" && (
                <img
                  src={ListIcon}
                  width={24}
                  height={24}
                  style={{
                    position: "absolute",
                    top: 15,
                    left: 20,
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(-1)}
                />
              )}
              <Box
                sx={styles.businessName(showBack)}
                onClick={handleBusinessShare}
              >
                <Typography sx={styles.businessText(theme, showBack)}>
                  {referral?.item?.business?.business}
                </Typography>
              </Box>
              <Box sx={{ borderBottom: "1px solid #D6D6DA" }} />
              {!referral?.availability?.private && (
                <Typography
                  onClick={handleBusinessShare}
                  sx={styles.viewMoreText(theme)}
                >
                  View Business
                </Typography>
              )}
            </Box>
            <Carousel {...settings}>
              {referral?.item?.itemImages?.map(
                (image: Image, index: number) => (
                  <div key={index} style={styles.carouselImageContainer as any}>
                    <img
                      src={parseImageUrl(image?.uri)}
                      alt={`Item ${index + 1}`}
                      style={styles.carouselImage as any}
                    />
                    <Box sx={styles.locationTag(theme)}>
                      <img
                        src={LocationIcon}
                        alt="Location Icon"
                        style={styles.locationImage as any}
                      />
                      {formatAddress(
                        referral.item.business?.address1,
                        referral.item.business?.city,
                        referral.item.business?.state,
                      )}
                    </Box>
                  </div>
                ),
              )}
            </Carousel>
          </Box>
          <Box sx={styles.detailsContainer}>
            <Box sx={styles.detailsInnerBox}>
              <Typography sx={styles.title(theme)}>
                {referral?.item?.itemDetails[0]?.item_name}
              </Typography>
              <Typography sx={styles.subtitle(theme)}>
                {referral?.item?.itemDetails[0]?.headline}
              </Typography>
              <Box sx={styles.infoContainer}>
                <>
                  {referral?.item?.itemDetails[0] &&
                    referral?.item?.itemDetails[0]?.max_guest > 0 && (
                      <>
                        <Box sx={styles.infoItem}>
                          <img src={CapacityIcon} alt="capacity-icon" />
                          <Typography sx={styles.itemText(theme)}>
                            {referral?.item?.itemDetails[0]?.max_guest}max
                          </Typography>
                        </Box>
                      </>
                    )}
                </>
                {referral.item.itemDetails[0].duration_applicable &&
                  referral.item.itemDetails[0].duration && (
                    <Box sx={styles.infoItem}>
                      <img src={TimeIcon} alt="time-icon" />
                      <Typography sx={styles.itemText(theme)}>
                        {formatDurationTime(
                          referral.item.itemDetails[0].duration,
                        )}{" "}
                        hr
                      </Typography>
                    </Box>
                  )}
                {formattedAgeRestriction && (
                  <Box sx={styles.infoItem}>
                    <img src={AgeRestriction} alt="age-icon" />
                    <Typography sx={styles.itemText(theme)}>
                      {formattedAgeRestriction}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={styles.divider}></Box>
              <Box mt={2}>
                {referral?.item?.itemDetails[0]?.description && (
                  <InfoSection
                    title="Description"
                    content={referral?.item?.itemDetails[0]?.description}
                  />
                )}
              </Box>
              {!showAll && (
                <Typography
                  sx={styles.seeMoreText(theme)}
                  onClick={() => setShowAll(true)}
                >
                  See more
                </Typography>
              )}
              {showAll && (
                <>
                  {referral?.item?.itemDetails[0]?.whats_included && (
                    <InfoSection
                      title="What's Included"
                      content={referral?.item?.itemDetails[0]?.whats_included}
                    />
                  )}
                  {referral?.item?.itemDetails[0]?.additional_info && (
                    <InfoSection
                      title="Additional Info"
                      content={referral?.item?.itemDetails[0]?.additional_info}
                    />
                  )}
                  {referral?.item?.itemDetails[0]?.cancellation_policy && (
                    <InfoSection
                      title="Cancellation Policy"
                      content={
                        referral?.item?.itemDetails[0]?.cancellation_policy
                      }
                    />
                  )}
                  <Typography
                    sx={styles.seeMoreText(theme)}
                    onClick={() => setShowAll(false)}
                  >
                    See less
                  </Typography>
                </>
              )}
              {referral?.availability?.private && (
                <Box sx={styles.bookingDetailsContainer}>
                  <Typography sx={styles.bookingDetailsText}>
                    Booking Details
                  </Typography>
                  <Box sx={styles.iconText}>
                    <img
                      src={calender}
                      alt="calender icon"
                      style={styles.calendarIcon}
                    />
                    <Typography sx={styles.bookingDetailTypography}>
                      {checkoutDate(referral?.availability?.start_time)}
                    </Typography>
                  </Box>
                  <Box sx={styles.iconText}>
                    <img
                      src={clock}
                      alt="clock icon"
                      style={styles.clockIcon}
                    />
                    <Typography sx={styles.bookingDetailTypography}>
                      {checkoutTime(referral?.availability?.start_time)} -
                      {checkoutTime(referral?.availability?.end_time)}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box sx={styles.dateAndTimeContainer}>
                <DateAndTime
                  itemType={referral?.item?.item_type}
                  handleDateChange={handleDateChange}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  isCalendarVisible={isCalendarVisible}
                  setIsCalendarVisible={setIsCalendarVisible}
                  setSelectedTime={setSelectedTime}
                  selectedTicket={selectedTicket}
                  setSelectedTicket={setSelectedTicket}
                  availableTimes={availableTimes}
                  availabilities={referral?.item?.availabilities}
                  price_description={
                    referral?.item?.itemDetails[0]?.price_description
                  }
                  unit_name={referral?.item?.itemDetails[0]?.unit_name}
                  ticket={referral?.availability.quantity}
                  basePrice={referral?.item?.itemDetails[0]?.price}
                  price={price}
                  setPrice={setPrice}
                  availability={referral?.availability}
                  onMonthChange={onMonthChange}
                  isLoadingAvailabilities={isLoadingAvailabilities}
                />
              </Box>
              {referral?.item?.itemDetails[0]?.offering && (
                <Box sx={styles.promotionalContainer}>
                  <Box sx={styles.promotionalIconContainer}>
                    <img
                      src={RedFireIcon}
                      alt="fire-icon"
                      style={styles.redFireIcon}
                    />
                  </Box>
                  <Box sx={styles.promotionalTextContainer}>
                    <Typography sx={styles.promoTitle(theme)}>
                      Book Now and Receive
                    </Typography>
                    <Typography sx={styles.promoSubtitle(theme)}>
                      {referral?.item?.itemDetails[0]?.offering}
                    </Typography>
                  </Box>
                  <Box sx={styles.promotionalIconContainer}>
                    <img
                      src={RedFireIcon}
                      alt="fire-icon"
                      style={styles.redFireIcon}
                    />
                  </Box>
                </Box>
              )}
              <AdditionalInformation
                price={price}
                setPrice={setPrice}
                unit_name={referral?.item?.itemDetails[0]?.unit_name}
                ticket={referral?.item?.itemDetails[0]?.capacity}
                basePrice={referral?.item?.itemDetails[0]?.price}
                additionalOptions={referral?.item?.additionalOptions}
                setAdditionalOptions={setAdditionalOptionsValues}
              />
              <Box sx={styles.cancellationPolicyContainer}>
                <Box sx={styles.cancellationHeader}>
                  <img
                    src={CancellationIcon}
                    alt="CancellationIcon"
                    style={styles.cancellationIcon}
                  />
                  <Typography sx={styles.cancellationPolicyTitle(theme)}>
                    Cancellation Policy
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={styles.cancellationPolicyText}>
                    {isExpanded
                      ? referral?.item?.itemDetails[0]?.cancellation_policy
                      : `${referral?.item?.itemDetails[0]?.cancellation_policy?.slice(0, 100)}${
                          referral?.item?.itemDetails[0]?.cancellation_policy
                            ?.length > 100
                            ? "..."
                            : ""
                        }`}
                  </Typography>
                  {referral?.item?.itemDetails[0]?.cancellation_policy?.length >
                    100 && (
                    <Typography
                      sx={styles.cancellationPolicyToggle}
                      onClick={() => setIsExpanded((prev) => !prev)}
                    >
                      {isExpanded ? "See Less" : "See More"}
                    </Typography>
                  )}
                </Box>
              </Box>
              <CustomerInformationForm
                price={price}
                setPrice={setPrice}
                formData={formData}
                handleSelect={handleSelect}
                comment={comment}
                setComment={setComment}
                influencerTypesData={influencerTypesData}
                offering={referral?.item?.itemDetails[0]?.offering}
                additionalOptions={referral?.item?.additionalOptions}
                setAdditionalOptions={setAdditionalOptionsValues}
              />

              {/* Updated Referred By Section */}
              {referredByUserData && (
                <Box>
                  <Typography sx={styles.referredByText(theme)}>
                    Referred by {referredByUserData.isBusiness}
                  </Typography>
                  <Box sx={styles.referredByContainer}>
                    <img
                      src={referredByUserData.avatar || "/default-avatar.png"}
                      alt={`${referredByUserData.name} avatar`}
                      style={{
                        width: "33px",
                        height: "33px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                    <Typography sx={styles.userNameText(theme)}>
                      {referredByUserData.name}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={styles.poweredByContainer}>
                <Typography sx={styles.poweredByText}>Powered by</Typography>
                <Box sx={styles.logoImageContainer}>
                  <img src={DarkLogo} alt="logo" style={styles.logoImage} />
                </Box>
              </Box>
              <Box component="footer" sx={styles.footer}>
                <Box sx={styles.footerInner}>
                  <Box sx={styles.priceContainer}>
                    <Typography sx={styles.priceValue(theme)}>
                      {currencyFormat(price)}
                    </Typography>
                    <Typography sx={styles.priceFee(theme)}>
                      {`+ ${currencyFormat(calculateFees(price))} (Tax & Fees)`}
                    </Typography>
                  </Box>
                  <Box onClick={handleBookNowClick} sx={styles.bookNowButton}>
                    <img
                      src={FireIcon}
                      alt="fire-icon"
                      style={styles.fireIcon}
                    />
                    <Typography sx={styles.bookNowText}>BOOK NOW</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
      {!showSuccess && showCheckout && (
        <StripePayment
          setShowCheckout={showCheckout}
          price={price}
          currencyFormat={currencyFormat}
          handleGetStripeClientSecret={handleGetStripeClientSecret}
          handleCheckoutClick={handleCheckoutClick}
          setShowSuccess={setShowSuccess}
          setName={setName}
          setPhoneNumber={setPhoneNumber}
          setEmail={setEmail}
          name={name}
          phoneNumber={phoneNumber}
          email={email}
          setIsLoading={setItemsLoading}
        />
      )}
      {showSuccess && showCheckout && (
        <SuccessModal
          bookingId={bookingId}
          referral={referral}
          price={price + calculateFees(price)}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          endTime={referral?.availability?.end_time}
        />
      )}
    </>
  );
}
